import { Children, isValidElement, ReactElement, ReactNode } from 'react';
import { Route, RouteProps, Routes } from 'react-router-dom';
import { compatWrapper } from '@backstage/core-compat-api';
import type { CreateAppOptions } from '@backstage/frontend-defaults';
import {
  coreExtensionData,
  ExtensionBoundary,
} from '@backstage/frontend-plugin-api';

type ExtensionFactoryMiddleware = Exclude<
  NonNullable<
    NonNullable<CreateAppOptions['advanced']>['extensionFactoryMiddleware']
  >,
  readonly unknown[]
>;

// Renders the pages that convertLegacyAppRoot converts from the FlatRoutes
// routes synchronously, as the legacy app did.
//
// convertLegacyAppRoot (@backstage/core-compat-api 0.5.15,
// dist/collectLegacyRoutes.esm.js) turns each route into a PageBlueprint page
// whose loader returns the route element. PageBlueprint
// (@backstage/frontend-plugin-api 0.18.1, dist/blueprints/PageBlueprint.esm.js)
// renders that loader with ExtensionBoundary.lazy inside the page component,
// which creates a new React.lazy on every render. Every mount of a page
// therefore suspends at least once, although the element is already in memory:
//
// - On a cold load the page suspends after the app has shown a Suspense
//   fallback, and React 18 holds the reveal until 500 ms after that fallback
//   (FALLBACK_THROTTLE_MS), so the sidebar and the page appear about half a
//   second later than under the legacy app.
// - On in-app navigation the content area is empty for a moment on every page
//   change, including a return to a page already visited.
//
// PRD #22 §6.7 records the trace and the measurements.
//
// This middleware keeps the page extension that the conversion produced, with
// its path, route ref, routing shims, plugin and APIs, and replaces only its
// element with the route element inside ExtensionBoundary, without a lazy
// loader. The page layout that PageBlueprint adds is dropped: core-compat-api
// sets noHeader, under which @backstage/plugin-app's layout renders the element
// alone. Each plugin's own code still loads on first use through its
// createRoutableExtension, as under the legacy app.
//
// Maintenance condition: normalizeRoutePath and pageElement below copy two
// internals of core-compat-api that it does not export. If either drifts, the
// affected page falls back to the lazy path. App.test.tsx fails when a page is
// left lazy or when a page visited before does not render in the same act as
// the click. On an upgrade of @backstage/core-compat-api or
// @backstage/frontend-plugin-api, also repeat the cold-load and in-app
// navigation timings in PRD #22 §6.7.

/** core-compat-api's normalizeRoutePath (dist/normalizeRoutePath.esm.js). */
function normalizeRoutePath(path: string): string {
  let normalized = path;
  while (normalized.endsWith('/') || normalized.endsWith('*')) {
    normalized = normalized.slice(0, -1);
  }
  while (normalized.startsWith('/')) {
    normalized = normalized.slice(1);
  }
  return normalized ? `/${normalized}` : '/';
}

/** The element collectLegacyRoutes gives the page for one route. */
function pageElement({ element, children }: RouteProps): ReactNode {
  if (!children) {
    return element;
  }
  return (
    <Routes>
      <Route path="*" element={element}>
        <Route path="*" element={children} />
      </Route>
    </Routes>
  );
}

/**
 * Returns the extension factory middleware for createApp's
 * advanced.extensionFactoryMiddleware option. `flatRoutes` is the same
 * FlatRoutes element that convertLegacyAppRoot receives.
 */
export function eagerLegacyPages(
  flatRoutes: ReactElement<{ children?: ReactNode }>,
): ExtensionFactoryMiddleware {
  const elementsByPath = new Map<string, ReactNode>();
  Children.forEach(flatRoutes.props.children, route => {
    if (!isValidElement<RouteProps>(route) || route.props.path === undefined) {
      return;
    }
    elementsByPath.set(
      normalizeRoutePath(route.props.path),
      pageElement(route.props),
    );
  });

  return (originalFactory, { node }) => {
    const output = originalFactory();
    if (!node.spec.id.startsWith('page:')) {
      return output;
    }
    const path = output.get(coreExtensionData.routePath);
    if (typeof path !== 'string' || !elementsByPath.has(path)) {
      return output;
    }
    const element = elementsByPath.get(path);
    return Array.from(output, value =>
      value.id === coreExtensionData.reactElement.id
        ? coreExtensionData.reactElement(
            <ExtensionBoundary node={node}>
              {compatWrapper(element)}
            </ExtensionBoundary>,
          )
        : value,
    );
  };
}
